/**
 * Hook for fetching intraday ML trading signals — multi-ticker, multi-algorithm.
 *
 * Fetches status for ALL tickers (TQQQ, UPRO) and both V1+V2 algorithms.
 */

import { useState, useEffect, useCallback } from 'react';

const isLocalDev = typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const WORKER_URL = isLocalDev
  ? '/api/intraday-ml'
  : 'https://vectrax-intraday-ml.thegpc806.workers.dev';

const LIVE_PRICES_URL = isLocalDev
  ? '/api/live-prices'
  : 'https://vectrax-live-prices.thegpc806.workers.dev';

const TICKERS = ['TQQQ', 'UPRO', 'SQQQ', 'SPXU'];

export default function useIntradayML() {
  const [statuses, setStatuses] = useState({});       // { TQQQ: { models: {}, models_v2: {} }, UPRO: ... }
  const [transactions, setTransactions] = useState({}); // keyed by `${ticker}_${timeframe}_${algo}`
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [livePrices, setLivePrices] = useState({});   // { TQQQ: 38.54, UPRO: 90.47, ... }
  const [marketStatus, setMarketStatus] = useState({ market_open: false, extended_hours: false });

  const [sessionMode, setSessionMode] = useState(() => {
    try { return localStorage.getItem('intraday_ml_session_mode') || 'regular'; } catch { return 'regular'; }
  });

  const changeSessionMode = useCallback((mode) => {
    setSessionMode(mode);
    try { localStorage.setItem('intraday_ml_session_mode', mode); } catch {}
  }, []);

  const fetchAllStatuses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const results = await Promise.all(
        TICKERS.map(async (t) => {
          const resp = await fetch(`${WORKER_URL}?ticker=${t}&session_mode=${sessionMode}`);
          if (!resp.ok) throw new Error(`API error for ${t}: ${resp.status}`);
          const data = await resp.json();
          return { ticker: t, data };
        })
      );
      const newStatuses = {};
      for (const { ticker, data } of results) {
        newStatuses[ticker] = data;
      }
      setStatuses(newStatuses);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching statuses:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [sessionMode]);

  const fetchTransactions = useCallback(async (ticker, timeframe = '5min', algo = 'all') => {
    const key = `${ticker}_${timeframe}_${algo}_${sessionMode}`;
    try {
      const resp = await fetch(
        `${WORKER_URL}?ticker=${ticker}&transactions=true&timeframe=${timeframe}&algo=${algo}&limit=2000&session_mode=${sessionMode}`
      );
      if (!resp.ok) return;
      const data = await resp.json();
      // Tag each transaction with ticker
      if (data.transactions) {
        data.transactions = data.transactions.map(t => ({ ...t, ticker }));
      }
      setTransactions(prev => ({ ...prev, [key]: data }));
    } catch (err) {
      console.error('Error fetching transactions:', err);
    }
  }, [sessionMode]);

  const fetchAlerts = useCallback(async () => {
    try {
      const results = await Promise.all(
        TICKERS.map(async (t) => {
          const resp = await fetch(`${WORKER_URL}?ticker=${t}&alerts=true&limit=50`);
          if (!resp.ok) return [];
          const data = await resp.json();
          return (data.alerts || []).map(a => ({ ...a, ticker: t }));
        })
      );
      setAlerts(results.flat().sort((a, b) =>
        (b.created_at || '').localeCompare(a.created_at || '')
      ));
    } catch (err) {
      console.error('Error fetching alerts:', err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchAllStatuses();
    fetchAlerts();
    // Fetch transactions for all tickers/timeframes
    for (const t of TICKERS) {
      fetchTransactions(t, '5min', 'all');
      fetchTransactions(t, '1h', 'all');
    }
  }, [fetchAllStatuses, fetchAlerts, fetchTransactions]);

  // Auto-refresh every 10 seconds (daemon pushes prices every 10s)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchAllStatuses();
    }, 5000);
    return () => clearInterval(interval);
  }, [fetchAllStatuses]);

  // Live price polling (every 30 seconds, independent of scorer)
  const fetchLivePrices = useCallback(async () => {
    try {
      const resp = await fetch(LIVE_PRICES_URL);
      if (resp.ok) {
        const data = await resp.json();
        if (data.prices) {
          const priceMap = {};
          for (const [ticker, info] of Object.entries(data.prices)) {
            priceMap[ticker] = info.price;
          }
          setLivePrices(priceMap);
        }
        // Extract market status from live-prices response (Alpaca Clock API)
        if (data.market_open !== undefined) {
          setMarketStatus({ market_open: data.market_open, extended_hours: data.extended_hours || false });
        }
      }
    } catch (e) { /* silent — prices are supplementary */ }
  }, []);

  useEffect(() => {
    fetchLivePrices();
    const priceInterval = setInterval(fetchLivePrices, 30000);
    return () => clearInterval(priceInterval);
  }, [fetchLivePrices]);

  // Merge live prices into statuses — update price field for display
  const statusesWithLivePrices = Object.fromEntries(
    Object.entries(statuses).map(([ticker, data]) => {
      const livePrice = livePrices[ticker];
      if (!livePrice) return [ticker, data];
      // Clone and update price in all model entries
      const updated = { ...data, live_price: livePrice };
      return [ticker, updated];
    })
  );

  const refresh = useCallback(() => {
    fetchAllStatuses();
    fetchAlerts();
    fetchLivePrices();
    for (const t of TICKERS) {
      fetchTransactions(t, '5min', 'all');
      fetchTransactions(t, '1h', 'all');
    }
  }, [fetchAllStatuses, fetchAlerts, fetchLivePrices, fetchTransactions]);

  return {
    tickers: TICKERS,
    statuses: statusesWithLivePrices,
    livePrices,
    marketStatus,
    transactions,
    alerts,
    loading,
    error,
    lastUpdated,
    refresh,
    fetchTransactions,
    sessionMode,
    changeSessionMode,
  };
}
